import PropTypes from "prop-types";
import { useFieldArray, useFormContext } from "react-hook-form";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FormRecordPicker,
  FormToggle,
  RemoveButton,
  SectionCard,
} from "components/BaseForm";
import PointOfContactService from "services/PointOfContact";

const fetchPointsOfContact = search =>
  PointOfContactService.getForFilters(search ? { search_query: search } : null);

const PointsOfContactSection = ({ savedNames }) => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "points_of_contact",
  });

  // The first contact defaults to both roles; each later one to reporting only.
  const addRow = () =>
    append({
      point_of_contact_id: null,
      is_launch_contact: fields.length === 0,
      is_reporting_contact: true,
    });

  return (
    <SectionCard title="Points of Contact">
      {fields.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:min-w-64">Point of Contact</TableHead>
              <TableHead className="tw:w-36">Launch Contact</TableHead>
              <TableHead className="tw:w-36">Reporting Contact</TableHead>
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <FormRecordPicker
                    name={`points_of_contact.${index}.point_of_contact_id`}
                    placeholder="Pick a Point of Contact"
                    fetchOptions={fetchPointsOfContact}
                    optionsKey="ro-point-of-contact"
                    display={
                      field.point_of_contact_id != null
                        ? {
                            id: field.point_of_contact_id,
                            label: savedNames[field.point_of_contact_id],
                          }
                        : undefined
                    }
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`points_of_contact.${index}.is_launch_contact`}
                    aria-label={`Point of contact ${index + 1} launch contact`}
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`points_of_contact.${index}.is_reporting_contact`}
                    aria-label={`Point of contact ${index + 1} reporting contact`}
                  />
                </TableCell>
                <TableCell>
                  <RemoveButton
                    label={`Remove point of contact ${index + 1}`}
                    onClick={() => remove(index)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw:self-start"
        onClick={addRow}
      >
        <PlusIcon data-icon="inline-start" /> Add Point of Contact
      </Button>
    </SectionCard>
  );
};

PointsOfContactSection.propTypes = {
  savedNames: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default PointsOfContactSection;
